import { useEffect, useState } from 'react';

import { Button, Dialog, Typography } from '@mui/material';

import TextInput from 'components/TextInput';
import api from 'utils/api';
import { useConstants } from 'utils/hooks';
import { CreateOwnerSchema, UpdateOwnerSchema } from 'utils/types';
import Table from './Table';
import { useRecoilValue } from 'recoil';
import { currentRole } from 'pages/Login/state';

const marginSize = 48;

const modalContainerStyle = {
  margin: `${marginSize}px`,
  backgroundColor: 'white',
  width: 800
};

const OwnerDetailsModal = ({
  open,
  onClose,
  owner,
  action,
  handleAction
}: {
  open: any;
  onClose: any;
  owner: any;
  action: any;
  handleAction: any;
}) => {
  const { CONSTANTS } = useConstants();
  const myCurrentRole = useRecoilValue(currentRole);
  const isCreate = action === 'CREATE';
  const [data, setData] = useState(
    isCreate
      ? {
          ownerIdStr: '',
          name: '',
          admins: [],
          developers: [],
          developerAdmins: []
        }
      : { ...owner }
  );
  const [currAdminEmail, setCurrAdminEmail] = useState('');
  const [currDevEmail, setCurrDevEmail] = useState('');
  const [currDevAdminEmail, setCurrDevAdminEmail] = useState('');

  const adminColumns: any = [
    {
      name: 'Admin Email',
      selector: (admin: any) => admin.email
    },
    {
      name: 'Admin Name',
      selector: (admin: any) => admin.name
    },
    {
      name: 'Action',
      selector: (admin: any) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Button
            type='button'
            variant='secondary'
            color='dark'
            data-cy='remove-admin-button'
            disabled={myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN}
            onClick={() => {
              const newData = {
                ...data,
                admins: data.admins.filter((obj: any) => obj.id !== admin.id)
              };
              setData(newData);
            }}
          >
            Remove
          </Button>
        </div>
      )
    }
  ];

  const devAdminColumns: any = [
    {
      name: 'Developer Admin Email',
      selector: (devAdmin: any) => devAdmin.email
    },
    {
      name: 'Developer Admin Name',
      selector: (devAdmin: any) => devAdmin.name
    },
    {
      name: 'Action',
      selector: (devAdmin: any) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Button
            type='button'
            variant='secondary'
            color='dark'
            data-cy='remove-devAdmin-button'
            disabled={myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN}
            onClick={() => {
              const newData = {
                ...data,
                developerAdmins: data.developerAdmins.filter(
                  (obj: any) => obj.id !== devAdmin.id
                )
              };
              setData(newData);
            }}
          >
            Remove
          </Button>
        </div>
      )
    }
  ];

  const developerColumns: any = [
    {
      name: 'Developer Email',
      selector: (dev: any) => dev.email
    },
    {
      name: 'Developer Name',
      selector: (dev: any) => dev.name
    },
    {
      name: 'Action',
      selector: (dev: any) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Button
            type='button'
            variant='secondary'
            color='dark'
            data-cy='remove-dev-button'
            disabled={
              myCurrentRole === CONSTANTS.ROLE.ROLE_ADMIN ||
              myCurrentRole === CONSTANTS.ROLE.ROLE_SUB_ADMIN
            }
            onClick={() => {
              const newData = {
                ...data,
                developers: data.developers.filter(
                  (obj: any) => obj.id !== dev.id
                )
              };
              setData(newData);
            }}
          >
            Remove
          </Button>
        </div>
      )
    }
  ];

  const handleGetAdmin = async () => {
    if (!currAdminEmail.length) {
      alert('Please enter Admin Email');
      return;
    }
    if (data.admins.some((admin: any) => admin.email === currAdminEmail)) {
      alert('Admin Email already added');
      return;
    }
    try {
      const adminRes = await api.get(`/admin/users/byEmail/${currAdminEmail}`);
      const adminData = adminRes.data;
      if (
        !adminData.roles.some(
          (role: any) =>
            role == CONSTANTS.ROLE.ROLE_ADMIN ||
            role == CONSTANTS.ROLE.ROLE_SUB_ADMIN
        )
      ) {
        alert('The user is not a ADMIN or SUB ADMIN.');
        return;
      }
      const newAdmins: any = [...data.admins];
      newAdmins.unshift({
        id: adminData.id,
        email: adminData.email,
        name: adminData.name
      });
      setData({
        ...data,
        admins: newAdmins
      });
      setCurrAdminEmail('');
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        alert('Admin Email does not exist in the database.');
      } else if (error.response && error.response.status === 400) {
        alert('Please check that the Admin Email is in the correct format.');
      } else {
        alert(error.response.data.message);
      }
    }
  };

  const handleGetDevAdmin = async () => {
    if (!currDevAdminEmail.length) {
      alert('Please enter Developer Admin Email');
      return;
    }
    if (
      data.developerAdmins.some(
        (devAdmin: any) => devAdmin.email === currDevAdminEmail
      )
    ) {
      alert('Developer Admin Email already added');
      return;
    }
    try {
      const adminRes = await api.get(
        `/admin/users/byEmail/${currDevAdminEmail}`
      );
      const adminData = adminRes.data;
      if (
        !adminData.roles.some(
          (role: any) => role == CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN
        )
      ) {
        alert('The user is not a Developer ADMIN.');
        return;
      }
      const newDevAdmins: any = [...data.developerAdmins];
      newDevAdmins.unshift({
        id: adminData.id,
        email: adminData.email,
        name: adminData.name
      });
      setData({
        ...data,
        developerAdmins: newDevAdmins
      });
      setCurrDevAdminEmail('');
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        alert('Developer Admin Email does not exist in the database.');
      } else if (error.response && error.response.status === 400) {
        alert(
          'Please check that the Developer Admin Email is in the correct format.'
        );
      } else {
        alert(error.response.data.message);
      }
    }
  };

  const handleGetDeveloper = async () => {
    if (!currDevEmail.length) {
      alert('Please enter Developer Email');
      return;
    }
    if (data.developers.some((dev: any) => dev.email === currDevEmail)) {
      alert('Developer Email already added');
      return;
    }
    try {
      const userRes = await api.get(`/admin/users/byEmail/${currDevEmail}`);
      const userData = userRes.data;
      if (
        !userData.roles.some(
          (role: any) => role == CONSTANTS.ROLE.ROLE_DEVELOPER
        )
      ) {
        alert('The user is not a DEVELOPER.');
        return;
      }
      const newDevs: any = [...data.developers];
      newDevs.unshift({
        id: userData.id,
        email: userData.email,
        name: userData.name
      });
      setData({
        ...data,
        developers: newDevs
      });
      setCurrDevEmail('');
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        alert('Developer Email does not exist in the database.');
      } else if (error.response && error.response.status === 400) {
        alert(
          'Please check that the Developer Email is in the correct format.'
        );
      } else {
        alert(error.response.data.message);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <div style={modalContainerStyle} className='owner-details'>
          <Typography
            variant='h2'
            color='blue.900'
            style={{ marginBottom: 15 }}
          >
            {isCreate ? 'Create Owner' : 'Owner Details'}:
          </Typography>
          <TextInput
            variant='outlined'
            id='ownerIdStr'
            name='ownerIdStr'
            label='Owner Id'
            dataCy='ownerIdStr'
            value={data.ownerIdStr}
            readOnly={myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN}
            onChange={(e: any) => {
              setData({ ...data, ownerIdStr: e.target.value });
            }}
            containerStyle={{ width: '100%', maxWidth: 400 }}
          />
          <TextInput
            variant='outlined'
            id='name'
            name='name'
            label='Owner Name'
            dataCy='ownerName'
            value={data.name}
            readOnly={myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN}
            onChange={(e: any) => {
              setData({ ...data, name: e.target.value });
            }}
            containerStyle={{ width: '100%', maxWidth: 400 }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <TextInput
              variant='outlined'
              id='admin'
              name='admin'
              label='Admins'
              dataCy='adminEmail'
              value={currAdminEmail}
              readOnly={myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN}
              placeholder='email@domain.com'
              containerStyle={{ width: '100%', maxWidth: 400 }}
              onChange={(e: any) => {
                setCurrAdminEmail(e.target.value);
              }}
            />
            <Button
              type='button'
              variant='primary'
              color='dark'
              onClick={handleGetAdmin}
              data-cy='add-admin-button'
              disabled={myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN}
              style={{
                marginLeft: 20
              }}
            >
              Add admin
            </Button>
          </div>
          {data.admins.length ? (
            <Table data={data.admins} columns={adminColumns} />
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <TextInput
              variant='outlined'
              id='devAdmin'
              name='devAdmin'
              label='Developer Admins'
              dataCy='devAdminEmail'
              value={currDevAdminEmail}
              readOnly={myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN}
              placeholder='email@domain.com'
              containerStyle={{ width: '100%', maxWidth: 400 }}
              onChange={(e: any) => {
                setCurrDevAdminEmail(e.target.value);
              }}
            />
            <Button
              type='button'
              variant='primary'
              color='dark'
              onClick={handleGetDevAdmin}
              disabled={myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN}
              data-cy='add-devAdmin-button'
              style={{
                marginLeft: 20
              }}
            >
              Add Developer admin
            </Button>
          </div>
          {data.developerAdmins.length ? (
            <Table data={data.developerAdmins} columns={devAdminColumns} />
          ) : null}
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <TextInput
              variant='outlined'
              id='buoyDev'
              name='buoyDev'
              label='Developers'
              value={currDevEmail}
              dataCy='devEmail'
              placeholder='email@domain.com'
              readOnly={
                myCurrentRole === CONSTANTS.ROLE.ROLE_ADMIN ||
                myCurrentRole === CONSTANTS.ROLE.ROLE_SUB_ADMIN
              }
              containerStyle={{ width: '100%', maxWidth: 400 }}
              onChange={(e: any) => {
                setCurrDevEmail(e.target.value);
              }}
            />
            <Button
              type='button'
              variant='primary'
              color='dark'
              onClick={handleGetDeveloper}
              data-cy='add-developer-button'
              disabled={
                myCurrentRole === CONSTANTS.ROLE.ROLE_ADMIN ||
                myCurrentRole === CONSTANTS.ROLE.ROLE_SUB_ADMIN
              }
              style={{
                marginLeft: 20
              }}
            >
              Add Developer
            </Button>
          </div>
          {data.developers.length ? (
            <Table data={data.developers} columns={developerColumns} />
          ) : null}
          <div
            style={{
              display: 'flex',
              margin: 20
            }}
          >
            <Button
              type='button'
              variant='secondary'
              color='dark'
              data-cy='close-button'
              onClick={() => {
                onClose();
              }}
              style={{ marginRight: 15 }}
            >
              Close
            </Button>
            <Button
              type='button'
              variant='primary'
              color='dark'
              data-cy='action-button'
              onClick={() => {
                const input = {
                  ...data,
                  admins: data.admins.map((admin: any) => admin.id),
                  developers: data.developers.map((dev: any) => dev.id),
                  developerAdmins: data.developerAdmins.map(
                    (devAd: any) => devAd.id
                  )
                };
                let validation: any;
                if (action === 'CREATE') {
                  validation = CreateOwnerSchema.safeParse(input);
                } else {
                  validation = UpdateOwnerSchema.safeParse(input);
                }
                if (!validation.success) {
                  alert(validation.error.issues[0].message);
                  return;
                }
                handleAction(validation.data);
              }}
            >
              {isCreate ? 'Create Owner' : 'Update Owner'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default OwnerDetailsModal;
